import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { getTenantBranding, TENANT_BRANDING_EVENT } from '../utils/tenantBranding';
import { uploadToSignedUrl, readFileAsDataUrl } from '../utils/uploadToSignedUrl';
import { useAdminPageSearch } from '../utils/useAdminPageSearch';
import {
  FaArrowLeft,
  FaChevronRight,
  FaLock,
  FaMobileAlt,
  FaShieldAlt,
  FaGlobe,
  FaFileAlt,
  FaBell,
  FaUsers,
  FaCopy,
  FaTimes,
  FaPlus,
  FaLink,
  FaCheckCircle,
  FaClock,
  FaPalette,
  FaCreditCard,
  FaPhone,
  FaStar,
  FaFileInvoiceDollar,
  FaVideo,
} from 'react-icons/fa';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { graphqlRequest } from '../utils/graphqlRequest';
import WhiteLabelSettings from './WhiteLabelSettings';
import ReviewSettings from './ReviewSettings';
import VideoMeetingSettings from './VideoMeetingSettings';
import BillingSubscription from './BillingSubscription';
import { adminPath, ADMIN_ROUTES } from '../constants/adminPortal';
import {
  isSuperAdminUser,
  shouldOpenAdminProfileHub,
  readStoredUserRoles,
} from '../utils/adminProfileRouting';
import {
  fetchAllCompanyUsers,
  isWorkforcePerson,
  toWorkforceRoleKey,
  workforceRoleLabel,
  WORKFORCE_PREDEFINED_ROLES,
} from './WorkForce/utils/workforcePeople';
import '../styles/AdminAccountSettings.css';

const SETTINGS_TAB_IDS = [
  'general',
  'security',
  'preferences',
  'team',
  'payments',
  'reviews',
  'communications',
  'meetings',
  'whiteLabel',
  'customDomain',
  'billing',
];

const CHANGE_PASSWORD_MUTATION = `
  mutation ChangeMyPassword($input: ChangePasswordInput!) {
    changeMyPassword(input: $input)
  }
`;

// Company General Information — the single source of truth used across the
// platform (invoices, quotations, payslips, etc.).
const COMPANY_FIELDS = `
  id
  companyName
  tradingName
  abn
  businessNumber
  companyEmail
  emailFrom
  replyTo
  companyPhone
  companyMobile
  companyWebsite
  unitNumber
  address
  suburb
  state
  postCode
  country
  payrollEmail
  contractorEmail
  invoicingEmail
  timeZone
  companyLogo
  emailNotificationsEnabled
  marketingEmailEnabled
  productUpdatesEnabled
  invoiceBankName
  invoiceAccountName
  invoiceBsb
  invoiceAccountNumber
  invoiceBankReference
`;

const COMMUNICATION_FIELDS = `
  id
  voipProvider
  voipAccountId
  voipPhoneNumber
  whatsappPhoneNumber
  voipSecretReference
  callRecordingEnabled
  callRecordingConsentNotice
  callRecordingRetentionDays
`;

const GET_COMPANY = `
  query GetCompany($id: String!) {
    company(id: $id) { ${COMPANY_FIELDS} }
  }
`;

// Primary load path — company + admin contact (phone often lives on the user).
const GET_MY_COMPANY = `
  query GetMyCompanyForSettings {
    getMyProfile {
      id
      contact
      company { ${COMPANY_FIELDS} }
    }
  }
`;

const UPDATE_COMPANY = `
  mutation UpdateCompany($input: UpdateCompanyInput!) {
    updateCompany(updateCompanyInput: $input) { ${COMPANY_FIELDS} }
  }
`;

const GET_MY_COMMUNICATION_SETTINGS = `
  query GetMyCommunicationSettings {
    getMyProfile {
      company { ${COMMUNICATION_FIELDS} }
    }
  }
`;

const UPDATE_COMMUNICATION_SETTINGS = `
  mutation UpdateCommunicationSettings($input: UpdateCompanyInput!) {
    updateCompany(updateCompanyInput: $input) { ${COMMUNICATION_FIELDS} }
  }
`;

const SET_VOIP_AUTH_TOKEN = `
  mutation SetVoipAuthToken($token: String!) {
    setVoipAuthToken(token: $token)
  }
`;

const GENERATE_UPLOAD_URL = `
  mutation GenerateUrlForImageUpload($files: [Fileupload!]!) {
    generateUrlForImageUploadClient(files: $files) {
      key
      url
    }
  }
`;

const GET_MY_DEVICE_SESSIONS = `
  query GetMyDeviceSessions {
    getMyDeviceSessions {
      sessionId
      device
      type
      location
      ipAddress
      lastActiveAt
      isCurrent
    }
  }
`;

const GET_MY_LOGIN_HISTORY = `
  query GetMyLoginHistory {
    getMyLoginHistory {
      userId
      name
      roles
      email
      ipAddress
      device
      loginMethod
      success
      failureReason
      occurredAt
    }
  }
`;

const GET_MY_TWO_FACTOR_SETTINGS = `
  query GetMyTwoFactorSettings {
    getMyTwoFactorSettings {
      googleEnabled
      microsoftEnabled
    }
  }
`;

const UPDATE_MY_TWO_FACTOR_SETTINGS = `
  mutation UpdateMyTwoFactorSettings($input: UpdateMyTwoFactorSettingsInput!) {
    updateMyTwoFactorSettings(input: $input)
  }
`;

const GET_ALL_USERS = `
  query GetAllUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        email
        roles
        role
        isActive
      }
      total
      totalPages
      page
    }
  }
`;

const REGISTER_USER_BY_ADMIN = `
  mutation RegisterUserByAdmin($RegisterUerByAdmin: RegisterUerByAdmin!) {
    registerUerByAdmin(RegisterUerByAdmin: $RegisterUerByAdmin) {
      id
      firstName
      lastName
      email
      roles
    }
  }
`;

const UPDATE_USER_BY_ADMIN_ROLES = `
  mutation UpdateUserByAdminRoles($id: String!, $input: UpdateUserByAdmin!) {
    updateUserByAdmin(id: $id, UpdateUserByAdmin: $input) {
      id
      roles
    }
  }
`;

const GET_STRIPE_CONFIG = `
  query GetStripeConfig {
    getStripeConfig {
      id
      publishableKey
      hasSecretKey
      hasWebhookSecret
      webhookEndpointId
      isActive
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_STRIPE_CONFIG = `
  mutation UpdateStripeConfig($input: UpdateStripeConfigInput!) {
    updateStripeConfig(input: $input) {
      id
      publishableKey
      webhookEndpointId
      isActive
    }
  }
`;

// ─── Custom Domain GraphQL ────────────────────────────────────────────────────

const CUSTOM_DOMAIN_FIELDS = `
  id
  domain
  domainType
  portalType
  branchScope
  status
  sslStatus
  verificationToken
  certificateArn
  cloudfrontDistributionId
  isDedicatedDistribution
  errorMessage
  lastCheckedAt
  sslIssuedAt
  sslExpiresAt
  sslAutoRenew
  verifiedAt
  createdAt
  dnsRecords { type name value }
`;

const GET_CUSTOM_DOMAINS = `
  query GetCustomDomains {
    getCustomDomains {
      ${CUSTOM_DOMAIN_FIELDS}
    }
  }
`;

const ADD_CUSTOM_DOMAIN = `
  mutation AddCustomDomain($input: AddCustomDomainInput!) {
    addCustomDomain(input: $input) {
      ${CUSTOM_DOMAIN_FIELDS}
    }
  }
`;

const VERIFY_CUSTOM_DOMAIN = `
  mutation VerifyCustomDomain($id: String!) {
    verifyCustomDomain(id: $id) {
      ${CUSTOM_DOMAIN_FIELDS}
    }
  }
`;

const REMOVE_CUSTOM_DOMAIN = `
  mutation RemoveCustomDomain($id: String!) {
    removeCustomDomain(id: $id)
  }
`;

const REISSUE_SSL = `
  mutation ReissueSSLCertificate($id: String!) {
    reissueSSLCertificate(id: $id) {
      ${CUSTOM_DOMAIN_FIELDS}
    }
  }
`;

const PASSWORD_RULES = {
  minCaps: 1,
  minSpecial: 1,
  minDigits: 1,
  minLength: 7,
  maxLength: 14,
};

const TEAM_ROLE_OPTIONS = WORKFORCE_PREDEFINED_ROLES;

function SettingRow({ icon, title, subtitle, action, onClick }) {
  const isInteractive = typeof onClick === 'function';
  return (
    <div
      className={`acct-row ${isInteractive ? 'clickable' : ''}`}
      onClick={onClick}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className="acct-row-left">
        <span className="acct-row-icon">{icon}</span>
        <div>
          <p className="acct-row-title">{title}</p>
          {subtitle ? <p className="acct-row-subtitle">{subtitle}</p> : null}
        </div>
      </div>
      <div className="acct-row-action">{action}</div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      className={`acct-toggle ${checked ? 'on' : ''}`}
      onClick={disabled ? undefined : onChange}
      aria-pressed={checked}
      disabled={disabled}
    >
      <span className="acct-toggle-knob" />
    </button>
  );
}

function maskApiKey(key) {
  const str = String(key || '');
  if (!str) return '';
  if (str.length <= 10) return `${str.slice(0, 3)}...`;
  return `${str.slice(0, 6)}...${str.slice(-4)}`;
}

function resolveUploadUrl(rawUpload) {
  if (!rawUpload) return '';
  const value = String(rawUpload).trim();
  if (!value) return '';
  // Backend companyLogo ResolveField already returns a signed/absolute URL.
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:')) return value;
  if (value.startsWith('/')) return `${window.location.origin}${value}`;

  // Fallback if an S3 key is returned instead of a URL.
  const bucket = process.env.REACT_APP_AWS_BUCKET_NAME;
  const region = process.env.REACT_APP_AWS_REGION;
  if (bucket && region) {
    return `https://${bucket}.s3.${region}.amazonaws.com/${value}`;
  }
  return '';
}

function applyCompanyToForm(c, extras = {}) {
  return {
    companyName: c?.companyName || '',
    tradingName: c?.tradingName || '',
    // ABN may historically live in businessNumber on some rows.
    abn: c?.abn || c?.businessNumber || '',
    companyEmail: c?.companyEmail || c?.emailFrom || c?.replyTo || '',
    // Website signup stored phone on the user (`contact`), not companyPhone.
    companyPhone: c?.companyPhone || extras.contact || '',
    companyMobile: c?.companyMobile || '',
    companyWebsite: c?.companyWebsite || '',
    unitNumber: c?.unitNumber || '',
    address: c?.address || '',
    suburb: c?.suburb || '',
    state: c?.state || '',
    postCode: c?.postCode || '',
    country: c?.country || '',
    payrollEmail: c?.payrollEmail || '',
    contractorEmail: c?.contractorEmail || '',
    invoicingEmail: c?.invoicingEmail || '',
    timeZone: c?.timeZone || '',
    companyLogo: c?.companyLogo || '',
    emailNotificationsEnabled: Boolean(c?.emailNotificationsEnabled),
    marketingEmailEnabled: Boolean(c?.marketingEmailEnabled),
    productUpdatesEnabled: Boolean(c?.productUpdatesEnabled),
    invoiceBankName: c?.invoiceBankName || '',
    invoiceAccountName: c?.invoiceAccountName || '',
    invoiceBsb: c?.invoiceBsb || '',
    invoiceAccountNumber: c?.invoiceAccountNumber || '',
    invoiceBankReference: c?.invoiceBankReference || '',
  };
}

/** Retry a GraphQL query after stripping fields the live schema does not expose. */
async function graphqlRequestDropUnknownFields(query, variables = {}) {
  let nextQuery = query;
  let nextVariables = variables;
  for (let attempt = 0; attempt < 12; attempt += 1) {
    try {
      return await graphqlRequest(nextQuery, nextVariables);
    } catch (err) {
      const message = String(err?.message || '');
      const unknownField =
        message.match(/Cannot query field ["'](\w+)["']/i)?.[1] ||
        message.match(/Unknown field ["'](\w+)["']/i)?.[1] ||
        message.match(/Field ["'](\w+)["'] is not defined by type/i)?.[1] ||
        message.match(/column ["'](\w+)["'] does not exist/i)?.[1];
      if (!unknownField) throw err;
      const stripped = nextQuery.replace(
        new RegExp(`^\\s*${unknownField}\\s*$`, 'gm'),
        '',
      );
      const input = nextVariables?.input;
      if (input && Object.prototype.hasOwnProperty.call(input, unknownField)) {
        const { [unknownField]: _removed, ...rest } = input;
        nextVariables = { ...nextVariables, input: rest };
        continue;
      }
      if (stripped === nextQuery) throw err;
      nextQuery = stripped;
    }
  }
  throw new Error('Could not load company details.');
}

function applyCommunicationToForm(c) {
  return {
    voipProvider: c?.voipProvider || '',
    voipAccountId: c?.voipAccountId || '',
    voipPhoneNumber: c?.voipPhoneNumber || '',
    whatsappPhoneNumber: c?.whatsappPhoneNumber || '',
    voipSecretReference: c?.voipSecretReference || '',
    voipAuthToken: '',
    callRecordingEnabled: Boolean(c?.callRecordingEnabled),
    callRecordingConsentNotice:
      c?.callRecordingConsentNotice ||
      'This call may be recorded for quality, training and service documentation purposes.',
    callRecordingRetentionDays: Number(c?.callRecordingRetentionDays || 90),
  };
}

function getDeviceIconLabel(sessionType) {
  if (sessionType === 'mobile') return 'Mobile';
  if (sessionType === 'desktop') return 'Desktop';
  return 'Device';
}

function formatRelativeOrDate(value) {
  if (!value) return '';
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return '';
  const diffMs = Date.now() - ts;
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  return new Date(ts).toLocaleString();
}

function getPasswordMetrics(password) {
  return {
    caps: (password.match(/[A-Z]/g) || []).length,
    special: (password.match(/[^A-Za-z0-9]/g) || []).length,
    digits: (password.match(/[0-9]/g) || []).length,
    length: password.length,
  };
}

function getPasswordRuleChecks(password, confirmPassword = '') {
  const metrics = getPasswordMetrics(password || '');
  return {
    length:
      metrics.length >= PASSWORD_RULES.minLength &&
      metrics.length <= PASSWORD_RULES.maxLength,
    caps: metrics.caps >= PASSWORD_RULES.minCaps,
    special: metrics.special >= PASSWORD_RULES.minSpecial,
    digits: metrics.digits >= PASSWORD_RULES.minDigits,
    match: Boolean(password) && password === confirmPassword,
    metrics,
  };
}

function SettingsCard({ title, icon, children }) {
  return (
    <section className="acct-card st-card">
      <h2 className="st-card-title">
        <span className="st-card-icon">{icon}</span>
        {title}
      </h2>
      <div className="st-card-body">{children}</div>
    </section>
  );
}

export default function SettingsHub() {
  const navigate = useNavigate();
  const { get, patch } = useAdminPageSearch();
  const storedRoles = useMemo(() => readStoredUserRoles(), []);
  const isSuperAdmin = isSuperAdminUser(storedRoles);
  const urlTab = get('tab');
  const activeTab =
    SETTINGS_TAB_IDS.includes(urlTab) && (urlTab !== 'billing' || isSuperAdmin)
      ? urlTab
      : 'general';
  const setActiveTab = (id) => patch({ tab: id === 'general' ? '' : id });

  const companyId = Cookies.get('companyId');
  const canOpenProfileHub =
    isSuperAdminUser(storedRoles) || shouldOpenAdminProfileHub(storedRoles);

  // -------------------- General state --------------------
  const [companyLoading, setCompanyLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    tradingName: '',
    abn: '',
    companyEmail: '',
    companyPhone: '',
    companyMobile: '',
    companyWebsite: '',
    unitNumber: '',
    address: '',
    suburb: '',
    state: '',
    postCode: '',
    country: '',
    payrollEmail: '',
    contractorEmail: '',
    invoicingEmail: '',
    timeZone: '',
    companyLogo: '',
    emailNotificationsEnabled: false,
    marketingEmailEnabled: false,
    productUpdatesEnabled: false,
    invoiceBankName: '',
    invoiceAccountName: '',
    invoiceBsb: '',
    invoiceAccountNumber: '',
    invoiceBankReference: '',
    voipProvider: '', voipAccountId: '', voipPhoneNumber: '', whatsappPhoneNumber: '', voipSecretReference: '',
    callRecordingEnabled: false,
    callRecordingConsentNotice: 'This call may be recorded for quality, training and service documentation purposes.',
    callRecordingRetentionDays: 90,
  });
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [bankMessage, setBankMessage] = useState({ type: '', text: '' });
  const logoInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoMessage, setLogoMessage] = useState('');
  const [logoPreviewOverride, setLogoPreviewOverride] = useState('');

  // -------------------- Security state --------------------
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [twoFactor, setTwoFactor] = useState({ googleEnabled: false, microsoftEnabled: false });
  const [savingTwoFactor, setSavingTwoFactor] = useState(false);

  const passwordChecks = useMemo(
    () => getPasswordRuleChecks(newPassword, confirmPassword),
    [newPassword, confirmPassword],
  );
  const passwordMetrics = passwordChecks.metrics;
  const passwordMeetsRules =
    passwordChecks.length &&
    passwordChecks.caps &&
    passwordChecks.special &&
    passwordChecks.digits &&
    passwordChecks.match;

  // Device sessions (Save Browse / active devices)
  const [deviceSessions, setDeviceSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  // Login history
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginLoading, setLoginLoading] = useState(true);

  // -------------------- Team state --------------------
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamUsers, setTeamUsers] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'ADMIN',
  });

  const [roleSavingById, setRoleSavingById] = useState({});

  // -------------------- Integrations state --------------------
  const [stripeLoading, setStripeLoading] = useState(true);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [stripeForm, setStripeForm] = useState({
    publishableKey: '',
    secretKey: '',
    webhookSecret: '',
  });
  const [stripeMessage, setStripeMessage] = useState({ type: '', text: '' });
  const [savingStripe, setSavingStripe] = useState(false);

  // -------------------- Custom Domain state --------------------
  const [customDomainForm, setCustomDomainForm] = useState({
    domain: '',
    domainType: 'SUBDOMAIN',
    portalType: 'CLIENT',
    branchScope: 'ALL',
  });
  const [customDomainAdding, setCustomDomainAdding] = useState(false);
  const [customDomainMessage, setCustomDomainMessage] = useState({ type: '', text: '' });
  const [customDomains, setCustomDomains] = useState([]);
  const [customDomainsLoading, setCustomDomainsLoading] = useState(false);
  // The domain whose DNS records are currently shown in the DNS panel
  const [activeDnsRecord, setActiveDnsRecord] = useState(null);

  const loadCompany = async () => {
    setCompanyLoading(true);
    setLogoMessage('');
    try {
      let c = null;
      let contact = '';

      // Prefer profile: includes company + admin phone (`contact`).
      try {
        const profileRes = await graphqlRequestDropUnknownFields(GET_MY_COMPANY);
        const me = profileRes?.getMyProfile;
        c = me?.company || null;
        contact = me?.contact || '';
        if (c?.id) {
          Cookies.set('companyId', c.id, {
            expires: 1,
            path: '/',
            sameSite: 'strict',
            secure: window.location.protocol === 'https:',
          });
        }
      } catch (err) {
        console.warn('getMyProfile company load failed', err);
      }

      // Fallback: direct company query by cookie id (or id discovered above).
      const companyLookupId = c?.id || companyId;
      if (!c && companyLookupId) {
        const res = await graphqlRequestDropUnknownFields(GET_COMPANY, {
          id: companyLookupId,
        });
        c = res?.company || null;
      }

      if (!c) {
        throw new Error('Could not load company details. Please sign in again.');
      }

      setCompany(c);
      setCompanyForm((prev) => ({
        ...prev,
        ...applyCompanyToForm(c, { contact }),
      }));
    } catch (e) {
      console.error('Failed to load company', e);
      setLogoMessage(e?.message || 'Failed to load company details');
    } finally {
      setCompanyLoading(false);
    }
  };

  useEffect(() => {
    loadCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
    setIsSubmittingPassword(false);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
    resetPasswordForm();
  };

  const openPasswordModal = () => {
    resetPasswordForm();
    setIsPasswordModalOpen(true);
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all password fields.');
      return;
    }

    if (!passwordChecks.length) {
      setPasswordError(
        `Password length must be ${PASSWORD_RULES.minLength} to ${PASSWORD_RULES.maxLength} characters.`,
      );
      return;
    }

    if (!passwordChecks.caps) {
      setPasswordError('Password must include at least 1 uppercase letter.');
      return;
    }

    if (!passwordChecks.special) {
      setPasswordError('Password must include at least 1 special character.');
      return;
    }

    if (!passwordChecks.digits) {
      setPasswordError('Password must include at least 1 digit.');
      return;
    }

    if (!passwordChecks.match) {
      setPasswordError('New password and confirm password do not match.');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password.');
      return;
    }

    setIsSubmittingPassword(true);
    try {
      const response = await graphqlRequest(CHANGE_PASSWORD_MUTATION, {
        input: {
          currentPassword,
          newPassword,
        },
      });

      if (!response?.changeMyPassword) {
        setPasswordError('Unable to update password. Please try again.');
        return;
      }

      setPasswordSuccess('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      window.setTimeout(() => {
        setIsPasswordModalOpen(false);
        resetPasswordForm();
      }, 1200);
    } catch (error) {
      const raw = error?.message || 'Unable to update password. Please try again.';
      // ValidationPipe sometimes returns an array-like string; normalize it.
      const message = Array.isArray(raw) ? raw.join('. ') : String(raw);
      setPasswordError(message);
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const saveCompany = async (logoKeyOverride) => {
    setSavingCompany(true);
    setLogoMessage('');
    try {
      const logoValue =
        typeof logoKeyOverride === 'string' ? logoKeyOverride : undefined;
      // Only send companyLogo when we have a fresh S3 key (after upload).
      // Display URLs from the API must not be written back to the DB.
      const isHttpLogo =
        typeof companyForm.companyLogo === 'string' &&
        /^(https?:)?\/\//i.test(companyForm.companyLogo);

      await graphqlRequestDropUnknownFields(UPDATE_COMPANY, {
        input: {
          companyName: companyForm.companyName || undefined,
          tradingName: companyForm.tradingName || undefined,
          abn: companyForm.abn || undefined,
          businessNumber: companyForm.abn || undefined,
          companyEmail: companyForm.companyEmail || undefined,
          companyPhone: companyForm.companyPhone || undefined,
          companyMobile: companyForm.companyMobile || undefined,
          companyWebsite: companyForm.companyWebsite || undefined,
          unitNumber: companyForm.unitNumber || undefined,
          address: companyForm.address || undefined,
          suburb: companyForm.suburb || undefined,
          state: companyForm.state || undefined,
          postCode: companyForm.postCode || undefined,
          country: companyForm.country || undefined,
          payrollEmail: companyForm.payrollEmail || undefined,
          contractorEmail: companyForm.contractorEmail || undefined,
          invoicingEmail: companyForm.invoicingEmail || undefined,
          timeZone: companyForm.timeZone || undefined,
          ...(logoValue
            ? { companyLogo: logoValue }
            : !isHttpLogo && companyForm.companyLogo
              ? { companyLogo: companyForm.companyLogo }
              : {}),
          emailNotificationsEnabled: Boolean(companyForm.emailNotificationsEnabled),
          marketingEmailEnabled: Boolean(companyForm.marketingEmailEnabled),
          productUpdatesEnabled: Boolean(companyForm.productUpdatesEnabled),
        },
      });
      await loadCompany();
      setLogoMessage(logoValue ? 'Logo saved.' : 'Changes saved.');
      return true;
    } catch (e) {
      console.error(e);
      setLogoMessage(e?.message || 'Failed to save changes');
      return false;
    } finally {
      setSavingCompany(false);
    }
  };

  const loadCommunicationSettings = async () => {
    try {
      const response = await graphqlRequestDropUnknownFields(GET_MY_COMMUNICATION_SETTINGS);
      const settings = response?.getMyProfile?.company;
      if (settings) {
        setCompanyForm((previous) => ({
          ...previous,
          ...applyCommunicationToForm(settings),
        }));
      }
    } catch (error) {
      console.error('Failed to load communication settings', error);
      setLogoMessage(error?.message || 'Failed to load communication settings');
    }
  };

  useEffect(() => {
    if (activeTab === 'communications') loadCommunicationSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const saveCommunicationSettings = async () => {
    if (savingCompany) return;
    setSavingCompany(true);
    setLogoMessage('');
    try {
      if (companyForm.voipAuthToken?.trim()) {
        await graphqlRequest(SET_VOIP_AUTH_TOKEN, { token: companyForm.voipAuthToken.trim() });
      }
      const response = await graphqlRequest(UPDATE_COMMUNICATION_SETTINGS, {
        input: {
          voipProvider: companyForm.voipProvider || null,
          voipAccountId: companyForm.voipAccountId || null,
          voipPhoneNumber: companyForm.voipPhoneNumber || null,
          whatsappPhoneNumber: companyForm.whatsappPhoneNumber || null,
          callRecordingEnabled: Boolean(companyForm.callRecordingEnabled),
          callRecordingConsentNotice: companyForm.callRecordingConsentNotice || null,
          callRecordingRetentionDays: Number(companyForm.callRecordingRetentionDays || 90),
        },
      });
      const saved = response?.updateCompany;
      if (saved) {
        setCompanyForm((previous) => ({
          ...previous,
          ...applyCommunicationToForm(saved),
        }));
      }
      setLogoMessage('Communication settings saved.');
    } catch (error) {
      console.error(error);
      setLogoMessage(error?.message || 'Failed to save communication settings');
    } finally {
      setSavingCompany(false);
    }
  };

  // Renders a labelled text input bound to companyForm[key]. Plain helper (not a
  // component) so inputs keep focus across re-renders.
  const renderCompanyField = (key, label, extra = {}) => (
    <label className="st-field">
      <span className="st-field-label">{label}</span>
      <input
        className="st-field-input"
        value={companyForm[key] || ''}
        onChange={(e) => setCompanyForm((prev) => ({ ...prev, [key]: e.target.value }))}
        {...extra}
      />
    </label>
  );

  const handleUploadLogo = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      setLogoMessage('Please choose an image file.');
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setLogoPreviewOverride(localPreview);

    try {
      setLogoMessage('');
      setUploadingLogo(true);

      let stored = '';
      try {
        const contentType = file.type || 'image/jpeg';
        const uploadData = await graphqlRequest(GENERATE_UPLOAD_URL, {
          files: [{ fileName: file.name, contentType }],
        });
        const uploadTarget = uploadData?.generateUrlForImageUploadClient?.[0];
        if (uploadTarget?.url && uploadTarget?.key) {
          stored = await uploadToSignedUrl(
            uploadTarget.url,
            uploadTarget.key,
            file,
          );
        }
      } catch (uploadErr) {
        console.warn('S3 logo upload failed, using inline image', uploadErr);
      }

      if (!stored) {
        stored = await readFileAsDataUrl(file);
      }

      setCompanyForm((prev) => ({ ...prev, companyLogo: stored }));

      await graphqlRequest(
        `
          mutation UpdateCompanyLogo($input: UpdateCompanyInput!) {
            updateCompany(updateCompanyInput: $input) {
              id
              companyLogo
            }
          }
        `,
        { input: { companyLogo: stored } },
      );
      await loadCompany();
      setLogoMessage('Logo saved.');
      toast.success('Company logo updated.');
    } catch (e) {
      console.error(e);
      setLogoMessage(e?.message || 'Logo upload failed.');
      toast.error(e?.message || 'Logo upload failed.');
    } finally {
      setUploadingLogo(false);
      URL.revokeObjectURL(localPreview);
      setLogoPreviewOverride('');
    }
  };

  const loadSecurity = async () => {
    setSessionsLoading(true);
    setLoginLoading(true);

    const [sessionsOutcome, loginOutcome, twoFactorOutcome] = await Promise.allSettled([
      graphqlRequest(GET_MY_DEVICE_SESSIONS),
      graphqlRequest(GET_MY_LOGIN_HISTORY),
      graphqlRequest(GET_MY_TWO_FACTOR_SETTINGS),
    ]);

    if (sessionsOutcome.status === 'fulfilled') {
      const sessions = sessionsOutcome.value?.getMyDeviceSessions;
      setDeviceSessions(Array.isArray(sessions) ? sessions : []);
    } else {
      console.error('Failed to load device sessions', sessionsOutcome.reason);
      setDeviceSessions([]);
    }

    if (loginOutcome.status === 'fulfilled') {
      const history = loginOutcome.value?.getMyLoginHistory;
      setLoginHistory(Array.isArray(history) ? history : []);
    } else {
      console.error('Failed to load login history', loginOutcome.reason);
      setLoginHistory([]);
    }

    if (twoFactorOutcome.status === 'fulfilled') {
      const tf = twoFactorOutcome.value?.getMyTwoFactorSettings;
      setTwoFactor({
        googleEnabled: Boolean(tf?.googleEnabled),
        microsoftEnabled: Boolean(tf?.microsoftEnabled),
      });
    } else {
      console.error('Failed to load two-factor settings', twoFactorOutcome.reason);
    }

    setSessionsLoading(false);
    setLoginLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'security') loadSecurity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const saveTwoFactor = async (next) => {
    if (savingTwoFactor) return;
    setSavingTwoFactor(true);
    try {
      await graphqlRequest(UPDATE_MY_TWO_FACTOR_SETTINGS, {
        input: {
          googleEnabled: Boolean(next.googleEnabled),
          microsoftEnabled: Boolean(next.microsoftEnabled),
        },
      });
      setTwoFactor(next);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingTwoFactor(false);
    }
  };

  const loadTeam = async () => {
    setTeamLoading(true);
    try {
      const rows = await fetchAllCompanyUsers(graphqlRequest, GET_ALL_USERS);
      const workforce = (Array.isArray(rows) ? rows : [])
        .filter(isWorkforcePerson)
        .sort((a, b) => {
          const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
          const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
          return nameA.localeCompare(nameB);
        });
      setTeamUsers(workforce);
    } catch (e) {
      console.error(e);
      setTeamUsers([]);
      toast.error(e?.message || 'Failed to load team members.');
    } finally {
      setTeamLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'team') loadTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const inviteMember = async () => {
    const email = String(inviteForm.email || '').trim();
    const firstName = String(inviteForm.firstName || '').trim();
    const lastName = String(inviteForm.lastName || '').trim();
    const role = inviteForm.role;

    if (!email || !firstName || !lastName) {
      toast.error('Please enter first name, last name, and email.');
      return;
    }

    setInviting(true);
    try {
      // Generate a strong temporary password; backend emails it via registerUserByAdmin.
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
      let pass = '';
      const bytes = new Uint32Array(10);
      window.crypto?.getRandomValues(bytes);
      for (let i = 0; i < 10; i++) {
        pass += chars[bytes[i] % chars.length];
      }
      const tempPassword = `${pass}${Math.floor(Math.random() * 90 + 10)}`;

      const username = email.split('@')[0] || email;

      const res = await graphqlRequest(REGISTER_USER_BY_ADMIN, {
        RegisterUerByAdmin: {
          firstName,
          lastName,
          email,
          username,
          password: tempPassword,
          roles: [role],
        },
      });

      if (res?.registerUerByAdmin?.id) {
        setInviteForm({ firstName: '', lastName: '', email: '', role: 'ADMIN' });
        await loadTeam();
        toast.success(`Invitation email sent to ${email}.`);
      } else {
        toast.error('Invite failed. Please try again.');
      }
    } catch (e) {
      console.error(e);
      toast.error(
        e?.message ||
          'Failed to invite member. If this email already exists, use a different email.',
      );
    } finally {
      setInviting(false);
    }
  };

  const updateMemberRole = async (userId, nextRole) => {
    setRoleSavingById((prev) => ({ ...prev, [userId]: true }));
    try {
      await graphqlRequest(UPDATE_USER_BY_ADMIN_ROLES, {
        id: userId,
        input: {
          roles: [nextRole],
        },
      });
      await loadTeam();
      toast.success('Role updated.');
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Failed to update role.');
    } finally {
      setRoleSavingById((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const loadIntegrations = async () => {
    setStripeLoading(true);
    try {
      const res = await graphqlRequest(GET_STRIPE_CONFIG);
      const config = res?.getStripeConfig || null;
      setStripeConfig(config);
      setStripeForm({
        publishableKey: config?.publishableKey || '',
        secretKey: '',
        webhookSecret: '',
      });
    } catch (e) {
      console.error(e);
    } finally {
      setStripeLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'payments') {
      loadIntegrations();
      if (!company && !companyLoading) loadCompany();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const saveInvoiceBankDetails = async () => {
    if (savingBankDetails) return;
    setSavingBankDetails(true);
    setBankMessage({ type: '', text: '' });
    try {
      await graphqlRequest(UPDATE_COMPANY, {
        input: {
          invoiceBankName: companyForm.invoiceBankName || null,
          invoiceAccountName: companyForm.invoiceAccountName || null,
          invoiceBsb: companyForm.invoiceBsb || null,
          invoiceAccountNumber: companyForm.invoiceAccountNumber || null,
          invoiceBankReference: companyForm.invoiceBankReference || null,
        },
      });
      await loadCompany();
      setBankMessage({
        type: 'success',
        text: 'Bank details saved. New invoices will use these by default.',
      });
    } catch (e) {
      console.error(e);
      setBankMessage({
        type: 'error',
        text: e?.message || 'Failed to save bank details.',
      });
    } finally {
      setSavingBankDetails(false);
    }
  };

  const saveStripeConfig = async () => {
    if (savingStripe) return;
    setSavingStripe(true);
    setStripeMessage({ type: '', text: '' });
    try {
      const input = {};
      if (stripeForm.publishableKey) input.publishableKey = stripeForm.publishableKey.trim();
      if (stripeForm.secretKey) input.secretKey = stripeForm.secretKey.trim();
      if (stripeForm.webhookSecret) input.webhookSecret = stripeForm.webhookSecret.trim();
      await graphqlRequest(UPDATE_STRIPE_CONFIG, { input });
      const secretSaved = Boolean(input.secretKey) || Boolean(stripeConfig?.hasSecretKey);
      const webhookSaved = Boolean(input.webhookSecret) || Boolean(stripeConfig?.hasWebhookSecret);
      await loadIntegrations();
      setStripeForm((prev) => ({ ...prev, secretKey: '', webhookSecret: '' }));
      const saved = ['Publishable key saved.', secretSaved ? 'Secret key saved.' : 'Secret key is not saved.'];
      if (webhookSaved) saved.push('Webhook signing secret saved.');
      else saved.push('Webhook signing secret is not saved. Paste the whsec_ value from Stripe so paid invoices can be confirmed.');
      setStripeMessage({
        type: secretSaved && webhookSaved ? 'success' : 'error',
        text: saved.join(' '),
      });
    } catch (e) {
      console.error(e);
      setStripeMessage({ type: 'error', text: e?.message || 'Failed to save integration settings.' });
    } finally {
      setSavingStripe(false);
    }
  };

  const webhookUrl = useMemo(() => {
    // Mirror the existing backend docs (stripe README) example.
    try {
      const origin = window.location.origin.replace(/:\d+/, ':3000');
      return `${origin}/webhooks/stripe`;
    } catch {
      return '/webhooks/stripe';
    }
  }, []);

  // ── Load all custom domains for this tenant ──────────────────────────────
  const loadCustomDomains = async () => {
    setCustomDomainsLoading(true);
    try {
      const res = await graphqlRequest(GET_CUSTOM_DOMAINS);
      setCustomDomains(Array.isArray(res?.getCustomDomains) ? res.getCustomDomains : []);
    } catch (e) {
      console.error('Failed to load custom domains', e);
    } finally {
      setCustomDomainsLoading(false);
    }
  };

  // Auto-refresh the domain list while any domain is mid-flight (VERIFYING /
  // PROPAGATING), so users see status transitions without a manual reload.
  useEffect(() => {
    if (activeTab !== 'customDomain') return undefined;
    const inFlight = customDomains.some(
      (d) => d.status === 'VERIFYING' || d.status === 'PROPAGATING',
    );
    if (!inFlight) return undefined;
    const t = setInterval(() => loadCustomDomains(), 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, customDomains]);

  useEffect(() => {
    if (activeTab === 'customDomain') loadCustomDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Add a new domain → returns DNS records from backend ─────────────────
  const handleAddCustomDomain = async () => {
    const domain = String(customDomainForm.domain || '').trim();
    if (!domain) {
      setCustomDomainMessage({ type: 'error', text: 'Please enter a custom domain.' });
      return;
    }
    setCustomDomainAdding(true);
    setCustomDomainMessage({ type: '', text: '' });
    try {
      const res = await graphqlRequest(ADD_CUSTOM_DOMAIN, {
        input: {
          domain,
          domainType: customDomainForm.domainType,
          portalType: customDomainForm.portalType,
          branchScope: customDomainForm.branchScope,
        },
      });
      const added = res?.addCustomDomain;
      if (added) {
        setCustomDomainMessage({ type: 'success', text: `Domain "${domain}" added. Add the DNS records below to your provider, then we'll verify automatically.` });
        setActiveDnsRecord(added);                       // show DNS panel for this domain
        setCustomDomainForm({ domain: '', domainType: 'SUBDOMAIN', portalType: 'CLIENT', branchScope: 'ALL' });
        await loadCustomDomains();
      }
    } catch (e) {
      setCustomDomainMessage({ type: 'error', text: e?.message || 'Failed to add domain.' });
    } finally {
      setCustomDomainAdding(false);
    }
  };

  // ── Verify an existing domain (check DNS propagation) ───────────────────
  const handleVerifyDomain = async (id) => {
    try {
      const res = await graphqlRequest(VERIFY_CUSTOM_DOMAIN, { id });
      const updated = res?.verifyCustomDomain;
      if (updated) {
        setCustomDomains((prev) =>
          prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)),
        );
        // Keep the DNS/SSL panel in sync if it's showing this domain
        setActiveDnsRecord((prev) =>
          prev?.id === updated.id ? { ...prev, ...updated } : prev,
        );
      }
    } catch (e) {
      toast.error(e?.message || 'Verification failed. Ensure DNS records have propagated.');
    }
  };

  // ── Remove a domain ──────────────────────────────────────────────────────
  const handleRemoveDomain = async (id) => {
    try {
      await graphqlRequest(REMOVE_CUSTOM_DOMAIN, { id });
      setCustomDomains((prev) => prev.filter((d) => d.id !== id));
      if (activeDnsRecord?.id === id) setActiveDnsRecord(null);
    } catch (e) {
      toast.error(e?.message || 'Failed to remove domain.');
    }
  };

  // ── Reissue SSL for a domain ─────────────────────────────────────────────
  const handleReissueSSL = async (id) => {
    try {
      const res = await graphqlRequest(REISSUE_SSL, { id });
      const updated = res?.reissueSSLCertificate;
      if (updated) {
        setActiveDnsRecord(updated);
        await loadCustomDomains();
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to reissue SSL.');
    }
  };

  const tabs = useMemo(
    () => [
      { id: 'general', label: 'General', icon: <FaGlobe /> },
      { id: 'security', label: 'Security', icon: <FaLock /> },
      { id: 'preferences', label: 'Preferences', icon: <FaBell /> },
      { id: 'team', label: 'Team Settings', icon: <FaUsers /> },
      { id: 'payments', label: 'Payments', icon: <FaCreditCard /> },
      { id: 'reviews', label: 'Reviews', icon: <FaStar /> },
      { id: 'communications', label: 'Calls & WhatsApp', icon: <FaPhone /> },
      { id: 'meetings', label: 'Video Meetings', icon: <FaVideo /> },
      { id: 'whiteLabel', label: 'White Label', icon: <FaPalette /> },
      { id: 'customDomain', label: 'Custom Domain', icon: <FaLink /> },
      ...(isSuperAdmin
        ? [{ id: 'billing', label: 'Billing & Subscription', icon: <FaFileInvoiceDollar /> }]
        : []),
    ],
    [isSuperAdmin],
  );

  const companyLogoUrl = useMemo(() => {
    if (logoPreviewOverride) return logoPreviewOverride;
    const raw = companyForm.companyLogo || company?.companyLogo;
    if (typeof raw === 'string' && raw.startsWith('data:')) return raw;
    return resolveUploadUrl(raw);
  }, [logoPreviewOverride, company, companyForm.companyLogo]);

  // White-label branding — used as a fallback for the company logo preview so
  // the tenant's brand logo shows instead of plain initials.
  const [branding, setBranding] = useState(() => getTenantBranding());
  useEffect(() => {
    const onBranding = (e) => setBranding(e.detail || getTenantBranding());
    window.addEventListener(TENANT_BRANDING_EVENT, onBranding);
    return () => window.removeEventListener(TENANT_BRANDING_EVENT, onBranding);
  }, []);
  const previewLogoUrl = companyLogoUrl || branding?.logoUrl || '';

  return (
    <div className="acct-page st-page">
      <div className="acct-topbar st-topbar">
        <button type="button" className="acct-topbar-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </button>
        <h1>Settings</h1>
        <span className="acct-topbar-btn-placeholder" />
      </div>

      <nav className="st-tabs" role="tablist" aria-label="Settings sections">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`st-tab-btn ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
            role="tab"
            aria-selected={activeTab === t.id}
          >
            <span className="st-tab-icon">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </nav>

      {activeTab === 'general' && (
        <div className="st-layout">
          <SettingsCard title="Company Profile" icon={<FaFileAlt />}>
            {companyLoading ? (
              <p>Loading company...</p>
            ) : (
              <>
                <div className="st-logo-row">
                  <div className="st-logo-preview">
                    {previewLogoUrl ? (
                      <img src={previewLogoUrl} alt="Company logo" />
                    ) : (
                      <span>{String(companyForm.companyName || 'Company').slice(0, 2).toUpperCase()}</span>
                    )}
                  </div>

                  <div className="st-logo-actions">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleUploadLogo}
                    />
                    <button
                      type="button"
                      className="acct-btn-secondary"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                    </button>
                    <div className="st-help-text">Recommended size: 512x512px</div>
                  </div>
                </div>

                {/* ── Company Information ───────────────────────────── */}
                <div className="st-section-heading">Company Information</div>
                <div className="st-form-grid">
                  {renderCompanyField('companyName', 'Company Name')}
                  {renderCompanyField('tradingName', 'Trading Name')}
                  {renderCompanyField('abn', 'ABN Number')}
                  {renderCompanyField('companyPhone', 'Company Phone Number')}
                  {renderCompanyField('companyMobile', 'Company Mobile')}
                  {renderCompanyField('companyWebsite', 'Company Website')}
                  {renderCompanyField('timeZone', 'Timezone', { placeholder: 'UTC' })}
                </div>

                {/* ── Address Information ───────────────────────────── */}
                <div className="st-section-heading">Address Information</div>
                <div className="st-form-grid">
                  {renderCompanyField('unitNumber', 'Unit Number')}
                  {renderCompanyField('address', 'Street Address')}
                  {renderCompanyField('suburb', 'Suburb / Town / City')}
                  {renderCompanyField('state', 'State')}
                  {renderCompanyField('postCode', 'Postcode')}
                  {renderCompanyField('country', 'Country')}
                </div>

                {/* ── Email Addresses (by purpose) ──────────────────── */}
                <div className="st-section-heading">Email Addresses</div>
                <div className="st-help-text" style={{ marginBottom: 8 }}>
                  Assign emails to specific business functions. These are used on
                  the matching system-generated documents.
                </div>
                <div className="st-form-grid">
                  {renderCompanyField('companyEmail', 'General Company Email', { type: 'email' })}
                  {renderCompanyField('payrollEmail', 'Payroll Email', { type: 'email' })}
                  {renderCompanyField('contractorEmail', 'Contractor Management Email', { type: 'email' })}
                  {renderCompanyField('invoicingEmail', 'Invoicing / Billing Email', { type: 'email' })}
                </div>

                {logoMessage ? <p className="st-message">{logoMessage}</p> : null}

                <div className="st-form-actions">
                  <button
                    type="button"
                    className="acct-btn-primary"
                    onClick={saveCompany}
                    disabled={savingCompany}
                  >
                    {savingCompany ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}
          </SettingsCard>
        </div>
      )}

      {activeTab === 'security' && (
        <div className="st-layout">
          <SettingsCard title="Security" icon={<FaLock />}>
            <SettingRow
              icon={<FaLock />}
              title="Change Password"
              onClick={openPasswordModal}
              action={<FaChevronRight className="acct-chevron" />}
            />

            <SettingRow
              icon={<FaShieldAlt />}
              title="Two-Factor Authentication"
              subtitle="Choose Google or Microsoft authentication"
              action={
                <div className="st-two-factor-inline">
                  <Toggle
                    checked={twoFactor.googleEnabled}
                    onChange={() => saveTwoFactor({ ...twoFactor, googleEnabled: !twoFactor.googleEnabled })}
                    disabled={savingTwoFactor}
                  />
                  <Toggle
                    checked={twoFactor.microsoftEnabled}
                    onChange={() => saveTwoFactor({ ...twoFactor, microsoftEnabled: !twoFactor.microsoftEnabled })}
                    disabled={savingTwoFactor}
                  />
                </div>
              }
            />

            <div className="st-two-factor-labels">
              <span>Google</span>
              <span>Microsoft</span>
            </div>

            <SettingRow
              icon={<FaMobileAlt />}
              title="Face ID / Touch ID"
              subtitle="Quick login access (Coming Soon)"
              action={<Toggle checked={false} disabled />}
            />
          </SettingsCard>

          <div className="st-stack">
            <SettingsCard title="Login Activity" icon={<FaBell />}>
              {loginLoading ? (
                <p>Loading...</p>
              ) : (
                <div className="st-table">
                  {loginHistory.length === 0 ? (
                    <p className="st-muted">No login activity found.</p>
                  ) : (
                    loginHistory.slice(0, 5).map((row) => (
                      <div key={`${row.userId}-${row.occurredAt}`} className="st-table-row">
                        <div className="st-table-cell st-cell-main">
                          <div className="st-table-title">
                            {row.device || 'Device'}
                          </div>
                          <div className="st-table-sub">
                            {row.ipAddress || '-'}
                          </div>
                        </div>
                        <div className="st-table-cell st-cell-right">
                          <div className={`st-pill ${row.success ? 'ok' : 'bad'}`}>
                            {row.success ? 'Success' : 'Failed'}
                          </div>
                          <div className="st-table-time">{formatRelativeOrDate(row.occurredAt)}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              {canOpenProfileHub ? (
                <button
                  type="button"
                  className="st-link-out"
                  onClick={() =>
                    navigate(
                      `${adminPath(ADMIN_ROUTES.PROFILE_DASHBOARD)}?tab=login-history`,
                    )
                  }
                >
                  View full login history
                </button>
              ) : null}
            </SettingsCard>

            <SettingsCard title="Your devices" icon={<FaGlobe />}>
              {sessionsLoading ? (
                <p>Loading...</p>
              ) : (
                <div className="st-table">
                  {deviceSessions.length === 0 ? (
                    <p className="st-muted">No sessions found.</p>
                  ) : (
                    deviceSessions.slice(0, 5).map((s) => (
                      <div key={s.sessionId} className="st-table-row">
                        <div className="st-table-cell st-cell-main">
                          <div className="st-table-title">
                            {s.device || getDeviceIconLabel(s.type)}
                          </div>
                          <div className="st-table-sub">
                            {s.location || '-'}
                          </div>
                        </div>
                        <div className="st-table-cell st-cell-right">
                          <div className={`st-pill ${s.isCurrent ? 'ok' : 'bad'}`}>
                            {s.isCurrent ? 'This device' : 'Other'}
                          </div>
                          <div className="st-table-time">{formatRelativeOrDate(s.lastActiveAt)}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              <button
                type="button"
                className="st-link-out"
                onClick={() => navigate(adminPath(ADMIN_ROUTES.DEVICE_SESSIONS))}
              >
                Manage device sessions
              </button>
            </SettingsCard>
          </div>
        </div>
      )}

      {activeTab === 'preferences' && (
        <div className="st-layout">
          <SettingsCard title="Preferences" icon={<FaBell />}>
            {companyLoading ? (
              <p>Loading...</p>
            ) : (
              <>
                <div className="st-pref-row">
                  <div className="st-pref-text">
                    <div className="st-pref-title">Email Notifications</div>
                    <div className="st-pref-sub">Manage email notification preferences</div>
                  </div>
                  <Toggle
                    checked={companyForm.emailNotificationsEnabled}
                    onChange={() =>
                      setCompanyForm((prev) => ({
                        ...prev,
                        emailNotificationsEnabled: !prev.emailNotificationsEnabled,
                      }))
                    }
                  />
                </div>

                <div className="st-pref-row">
                  <div className="st-pref-text">
                    <div className="st-pref-title">Marketing Email</div>
                    <div className="st-pref-sub">Product announcements and marketing updates</div>
                  </div>
                  <Toggle
                    checked={companyForm.marketingEmailEnabled}
                    onChange={() =>
                      setCompanyForm((prev) => ({
                        ...prev,
                        marketingEmailEnabled: !prev.marketingEmailEnabled,
                      }))
                    }
                  />
                </div>

                <div className="st-pref-row">
                  <div className="st-pref-text">
                    <div className="st-pref-title">Product updates</div>
                    <div className="st-pref-sub">New features and improvements</div>
                  </div>
                  <Toggle
                    checked={companyForm.productUpdatesEnabled}
                    onChange={() =>
                      setCompanyForm((prev) => ({
                        ...prev,
                        productUpdatesEnabled: !prev.productUpdatesEnabled,
                      }))
                    }
                  />
                </div>

                <div className="st-form-actions">
                  <button type="button" className="acct-btn-primary" onClick={saveCompany} disabled={savingCompany}>
                    {savingCompany ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}
          </SettingsCard>
        </div>
      )}

      {activeTab === 'team' && (
        <div className="st-layout">
          <SettingsCard title="Team Settings" icon={<FaUsers />}>
            <div className="st-team-top">
              <h3 className="st-team-subtitle">Manage your team members and roles</h3>
            </div>

            {teamLoading ? (
              <p>Loading team...</p>
            ) : (
              <>
                <div className="st-team-invite">
                  <h4 className="st-invite-title">Invite New Member</h4>
                  <div className="st-invite-grid">
                    <label className="st-field">
                      <span className="st-field-label">First name</span>
                      <input
                        className="st-field-input"
                        value={inviteForm.firstName}
                        onChange={(e) => setInviteForm((prev) => ({ ...prev, firstName: e.target.value }))}
                      />
                    </label>
                    <label className="st-field">
                      <span className="st-field-label">Last name</span>
                      <input
                        className="st-field-input"
                        value={inviteForm.lastName}
                        onChange={(e) => setInviteForm((prev) => ({ ...prev, lastName: e.target.value }))}
                      />
                    </label>
                    <label className="st-field st-field--full">
                      <span className="st-field-label">Email</span>
                      <input
                        className="st-field-input"
                        type="email"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e.target.value }))}
                      />
                    </label>
                    <label className="st-field">
                      <span className="st-field-label">Role</span>
                      <select
                        className="st-field-input"
                        value={inviteForm.role}
                        onChange={(e) => setInviteForm((prev) => ({ ...prev, role: e.target.value }))}
                      >
                        {TEAM_ROLE_OPTIONS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="st-invite-action">
                      <button
                        type="button"
                        className="acct-btn-primary"
                        disabled={inviting}
                        onClick={inviteMember}
                      >
                        <FaPlus style={{ marginRight: 8 }} />
                        {inviting ? 'Inviting...' : 'Invite'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="st-team-list">
                  <h4 className="st-team-list-title">Members</h4>
                  {teamUsers.length === 0 ? (
                    <p className="st-muted">No team members found.</p>
                  ) : (
                    teamUsers.map((u) => {
                      const currentRole = toWorkforceRoleKey(u.roles, u.role);
                      const workforceLabel = workforceRoleLabel(u.roles, u.role);
                      return (
                        <div key={u.id} className="st-team-row">
                          <div className="st-team-row-left">
                            <div className="st-team-name">
                              {u.firstName} {u.lastName}
                            </div>
                            <div className="st-team-meta">
                              {u.email} · {workforceLabel} · {u.isActive ? 'Active' : 'Inactive'}
                            </div>
                          </div>
                          <div className="st-team-row-right">
                            <select
                              className="st-field-input st-field-input--small"
                              value={currentRole}
                              onChange={(e) => updateMemberRole(u.id, e.target.value)}
                              disabled={Boolean(roleSavingById[u.id])}
                            >
                              {TEAM_ROLE_OPTIONS.map((r) => (
                                <option key={r.value} value={r.value}>
                                  {r.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </SettingsCard>
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="st-layout st-payments-layout">
          <SettingsCard title="Invoice bank details" icon={<FaCreditCard />}>
            {companyLoading ? (
              <p className="st-muted">Loading bank details...</p>
            ) : (
              <>
                <p className="st-muted st-payments-intro">
                  These details appear on invoices for bank transfer payments. You can still
                  override them on a single invoice when editing.
                </p>
                <div className="st-integration-form st-integration-form--tight">
                  <label className="st-field">
                    <span className="st-field-label">Bank name</span>
                    <input
                      className="st-field-input"
                      value={companyForm.invoiceBankName || ''}
                      onChange={(e) =>
                        setCompanyForm((prev) => ({
                          ...prev,
                          invoiceBankName: e.target.value,
                        }))
                      }
                      placeholder="e.g. Commonwealth Bank"
                      autoComplete="off"
                    />
                  </label>
                  <label className="st-field">
                    <span className="st-field-label">Account name</span>
                    <input
                      className="st-field-input"
                      value={companyForm.invoiceAccountName || ''}
                      onChange={(e) =>
                        setCompanyForm((prev) => ({
                          ...prev,
                          invoiceAccountName: e.target.value,
                        }))
                      }
                      placeholder="e.g. SAS Cleaning Pty Ltd"
                      autoComplete="off"
                    />
                  </label>
                  <label className="st-field">
                    <span className="st-field-label">BSB</span>
                    <input
                      className="st-field-input"
                      value={companyForm.invoiceBsb || ''}
                      onChange={(e) =>
                        setCompanyForm((prev) => ({
                          ...prev,
                          invoiceBsb: e.target.value,
                        }))
                      }
                      placeholder="000-000"
                      autoComplete="off"
                    />
                  </label>
                  <label className="st-field">
                    <span className="st-field-label">Account number</span>
                    <input
                      className="st-field-input"
                      value={companyForm.invoiceAccountNumber || ''}
                      onChange={(e) =>
                        setCompanyForm((prev) => ({
                          ...prev,
                          invoiceAccountNumber: e.target.value,
                        }))
                      }
                      placeholder="Account number"
                      autoComplete="off"
                    />
                  </label>
                  <label className="st-field">
                    <span className="st-field-label">Default payment reference</span>
                    <input
                      className="st-field-input"
                      value={companyForm.invoiceBankReference || ''}
                      onChange={(e) =>
                        setCompanyForm((prev) => ({
                          ...prev,
                          invoiceBankReference: e.target.value,
                        }))
                      }
                      placeholder="Optional — e.g. invoice number instruction"
                      autoComplete="off"
                    />
                  </label>
                </div>
                {bankMessage?.text ? (
                  <p className={`st-message st-message--${bankMessage.type}`}>
                    {bankMessage.text}
                  </p>
                ) : null}
                <div className="st-form-actions st-form-actions--left">
                  <button
                    type="button"
                    className="acct-btn-primary"
                    onClick={saveInvoiceBankDetails}
                    disabled={savingBankDetails}
                  >
                    {savingBankDetails ? 'Saving...' : 'Save bank details'}
                  </button>
                </div>
              </>
            )}
          </SettingsCard>

          <SettingsCard title="Stripe Payment Settings" icon={<FaCreditCard />}>
            {stripeLoading ? (
              <p className="st-muted">Loading payment settings...</p>
            ) : (
              <>
                <p className="st-muted st-payments-intro">
                  Connect Stripe so clients can pay invoices from the client portal. Secret keys are
                  encrypted and never shown again after saving.
                </p>

                {stripeConfig?.publishableKey ? (
                  <div className="st-api-key-row st-api-key-row--card">
                    <div>
                      <div className="st-api-key-label">Saved publishable key</div>
                      <div className="st-api-key-value">
                        {maskApiKey(stripeConfig.publishableKey)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="acct-btn-secondary"
                      onClick={() => navigator.clipboard?.writeText(stripeConfig.publishableKey)}
                    >
                      <FaCopy style={{ marginRight: 8 }} />
                      Copy
                    </button>
                  </div>
                ) : null}

                <div className={`st-api-key-row st-api-key-row--card${stripeConfig?.hasSecretKey ? '' : ' is-missing'}`}>
                  <div>
                    <div className="st-api-key-label">Saved secret key</div>
                    <div className="st-api-key-value">
                      {stripeConfig?.hasSecretKey
                        ? 'Stored on the server. It is not shown again.'
                        : 'Not saved yet. Paste an sk_test_ or sk_live_ key and save.'}
                    </div>
                  </div>
                  <span className={`st-pill ${stripeConfig?.hasSecretKey ? 'ok' : 'bad'}`}>
                    {stripeConfig?.hasSecretKey ? 'Saved' : 'Missing'}
                  </span>
                </div>

                <div className={`st-api-key-row st-api-key-row--card${stripeConfig?.hasWebhookSecret ? '' : ' is-missing'}`}>
                  <div>
                    <div className="st-api-key-label">Saved webhook signing secret</div>
                    <div className="st-api-key-value">
                      {stripeConfig?.hasWebhookSecret
                        ? 'Stored on the server. Paid invoices can be confirmed.'
                        : 'Not saved. Paste the whsec_ secret from Stripe, then save.'}
                    </div>
                  </div>
                  <span className={`st-pill ${stripeConfig?.hasWebhookSecret ? 'ok' : 'bad'}`}>
                    {stripeConfig?.hasWebhookSecret ? 'Saved' : 'Missing'}
                  </span>
                </div>

                <h3 className="st-stripe-section-title">API credentials</h3>
                <div className="st-integration-form st-integration-form--tight">
                  <label className="st-field">
                    <span className="st-field-label">Publishable key (pk_test_ / pk_live_)</span>
                    <input
                      className="st-field-input"
                      value={stripeForm.publishableKey}
                      onChange={(e) =>
                        setStripeForm((prev) => ({ ...prev, publishableKey: e.target.value }))
                      }
                      placeholder="pk_test_..."
                      autoComplete="off"
                    />
                  </label>

                  <label className="st-field">
                    <span className="st-field-label">Secret key (sk_test_ / sk_live_)</span>
                    <input
                      className="st-field-input"
                      type="password"
                      value={stripeForm.secretKey}
                      onChange={(e) =>
                        setStripeForm((prev) => ({ ...prev, secretKey: e.target.value }))
                      }
                      placeholder={
                        stripeConfig?.hasSecretKey
                          ? 'Leave blank to keep the saved secret'
                          : 'Not saved yet — paste sk_test_ or sk_live_'
                      }
                      autoComplete="new-password"
                    />
                  </label>

                  <label className="st-field">
                    <span className="st-field-label">Webhook signing secret (whsec_)</span>
                    <input
                      className="st-field-input"
                      type="password"
                      value={stripeForm.webhookSecret}
                      onChange={(e) =>
                        setStripeForm((prev) => ({ ...prev, webhookSecret: e.target.value }))
                      }
                      placeholder={
                        stripeConfig?.hasWebhookSecret
                          ? 'Leave blank to keep the saved webhook secret'
                          : 'Not saved yet — paste whsec_ from Stripe'
                      }
                      autoComplete="new-password"
                    />
                  </label>
                </div>

                {stripeMessage?.text ? (
                  <p className={`st-message st-message--${stripeMessage.type}`}>{stripeMessage.text}</p>
                ) : null}

                <div className="st-form-actions st-form-actions--left">
                  <button
                    type="button"
                    className="acct-btn-primary"
                    onClick={saveStripeConfig}
                    disabled={savingStripe}
                  >
                    {savingStripe ? 'Saving...' : 'Save payment settings'}
                  </button>
                </div>
              </>
            )}
          </SettingsCard>

          <div className="st-stack">
            <SettingsCard title="Webhooks" icon={<FaBell />}>
              {stripeLoading ? (
                <p className="st-muted">Loading...</p>
              ) : (
                <>
                  <div className="st-webhook-row">
                    <div className="st-webhook-left">
                      <div className="st-webhook-url">{webhookUrl}</div>
                      <div className="st-webhook-sub">
                        Status: {stripeConfig?.hasWebhookSecret ? 'Signing secret saved' : 'Signing secret not saved'}
                      </div>
                    </div>
                    <div className="st-webhook-right">
                      <span className={`st-pill ${stripeConfig?.hasWebhookSecret ? 'ok' : 'bad'}`}>
                        {stripeConfig?.hasWebhookSecret ? 'Saved' : 'Missing'}
                      </span>
                    </div>
                  </div>

                  <p className="st-muted st-webhook-hint">
                    Add this endpoint in Stripe Dashboard → Developers → Webhooks. The backend
                    listens at <code>/webhooks/stripe</code>.
                  </p>

                  <div className="st-webhook-actions">
                    <button
                      type="button"
                      className="acct-btn-secondary"
                      onClick={() => navigator.clipboard?.writeText(webhookUrl)}
                    >
                      <FaCopy style={{ marginRight: 8 }} />
                      Copy URL
                    </button>
                  </div>
                </>
              )}
            </SettingsCard>
          </div>
        </div>
      )}

      {activeTab === 'communications' && (
        <div className="st-layout">
          <SettingsCard title="VOIP & WhatsApp Provider" icon={<FaPhone />}>
            <div className="st-help-text" style={{ marginBottom: 16 }}>Connect Twilio to call customers from your business number. &quot;Call now&quot; rings your own phone (from your profile) first, then connects you to the customer; duration and recordings are saved to the customer&apos;s timeline. Your Auth Token is stored encrypted and never shown again.</div>
            <div className="st-form-grid">
              <label className="st-field"><span className="st-field-label">Provider</span><select value={companyForm.voipProvider} onChange={(e) => setCompanyForm((p) => ({ ...p, voipProvider: e.target.value }))}><option value="">Not connected</option><option value="TWILIO">Twilio</option></select></label>
              {renderCompanyField('voipAccountId', 'Twilio Account SID', { placeholder: 'AC…' })}
              <label className="st-field"><span className="st-field-label">Twilio Auth Token</span><input type="password" autoComplete="new-password" value={companyForm.voipAuthToken || ''} placeholder={companyForm.voipSecretReference ? '•••••••• saved — type to replace' : 'Paste from the Twilio console'} onChange={(e) => setCompanyForm((p) => ({ ...p, voipAuthToken: e.target.value }))} /></label>
              {renderCompanyField('voipPhoneNumber', 'Twilio Phone Number', { placeholder: '+61…' })}
              {renderCompanyField('whatsappPhoneNumber', 'WhatsApp Business Number (sync coming soon)', { placeholder: '+61…' })}
              {renderCompanyField('callRecordingRetentionDays', 'Recording Retention (days)', { type: 'number' })}
            </div>
            <div className="st-pref-row" style={{ marginTop: 16 }}><div className="st-pref-text"><div className="st-pref-title">Record answered calls</div><div className="st-pref-sub">Enable only when the consent notice is configured and played.</div></div><Toggle checked={companyForm.callRecordingEnabled} onChange={() => setCompanyForm((p) => ({ ...p, callRecordingEnabled: !p.callRecordingEnabled }))} /></div>
            <label className="st-field" style={{ marginTop: 16 }}><span className="st-field-label">Recording consent notice</span><textarea rows="3" value={companyForm.callRecordingConsentNotice} onChange={(e) => setCompanyForm((p) => ({ ...p, callRecordingConsentNotice: e.target.value }))} /></label>
            <div className="st-help-text" style={{ marginTop: 10 }}>Recordings older than the retention period are deleted from Twilio automatically every night.</div>
            <div className="st-form-actions"><button type="button" className="acct-btn-primary" onClick={saveCommunicationSettings} disabled={savingCompany}>{savingCompany ? 'Saving…' : 'Save Communication Settings'}</button></div>
          </SettingsCard>
        </div>
      )}

      {activeTab === 'reviews' && <ReviewSettings SettingsCard={SettingsCard} />}

      {activeTab === 'meetings' && <VideoMeetingSettings SettingsCard={SettingsCard} />}

      {activeTab === 'billing' && <BillingSubscription />}

      {activeTab === 'whiteLabel' && (
        <WhiteLabelSettings company={company} companyForm={companyForm} />
      )}

      {activeTab === 'customDomain' && (
        <div className="st-layout">
          <div className="st-custom-domain-header">
            <h2 className="st-custom-domain-title">Custom Domain</h2>
            <p className="st-custom-domain-subtitle">Connect your own domain to your CRM instance</p>
          </div>

          <SettingsCard title="Add Custom Domain" icon={<FaLink />}>
            <p className="st-custom-domain-card-sub">Configure your custom domain settings</p>

            <div className="st-custom-domain-form">
              <div className="st-custom-domain-row">
                <label className="st-field st-field--grow">
                  <span className="st-field-label">Enter Custom Domain</span>
                  <input
                    className="st-field-input"
                    placeholder="admin.yourcompany.com"
                    value={customDomainForm.domain}
                    onChange={(e) =>
                      setCustomDomainForm((prev) => ({ ...prev, domain: e.target.value }))
                    }
                  />
                </label>

                <label className="st-field st-field--fixed">
                  <span className="st-field-label">Portal</span>
                  <select
                    className="st-field-input"
                    value={customDomainForm.portalType}
                    onChange={(e) =>
                      setCustomDomainForm((prev) => ({ ...prev, portalType: e.target.value }))
                    }
                  >
                    <option value="ADMIN">Admin Portal</option>
                    <option value="CLIENT">Client Portal</option>
                    <option value="EMPLOYEE">Employee Portal</option>
                  </select>
                </label>

                <label className="st-field st-field--fixed">
                  <span className="st-field-label">Domain Type</span>
                  <select
                    className="st-field-input"
                    value={customDomainForm.domainType}
                    onChange={(e) =>
                      setCustomDomainForm((prev) => ({ ...prev, domainType: e.target.value }))
                    }
                  >
                    <option value="SUBDOMAIN">Subdomain</option>
                    <option value="ROOT">Root Domain</option>
                    <option value="WILDCARD">Wildcard</option>
                  </select>
                </label>
              </div>

              <label className="st-field st-field--full">
                <span className="st-field-label">Branch Scope</span>
                <select
                  className="st-field-input"
                  value={customDomainForm.branchScope}
                  onChange={(e) =>
                    setCustomDomainForm((prev) => ({ ...prev, branchScope: e.target.value }))
                  }
                >
                  <option value="ALL">All Branches</option>
                  <option value="MAIN">Main Branch</option>
                  <option value="SPECIFIC">Specific Branch</option>
                </select>
              </label>

              {customDomainMessage?.text ? (
                <p className={`st-message st-message--${customDomainMessage.type}`}>
                  {customDomainMessage.type === 'success' ? (
                    <FaCheckCircle style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  ) : null}
                  {customDomainMessage.text}
                </p>
              ) : null}

              <div className="st-form-actions st-form-actions--left">
                <button
                  type="button"
                  className="acct-btn-primary"
                  onClick={handleAddCustomDomain}
                  disabled={customDomainAdding}
                >
                  {customDomainAdding ? 'Verifying...' : 'Verify Domain'}
                </button>
              </div>
            </div>
          </SettingsCard>

          {/* DNS + SSL row — shown when a domain has been added/selected */}
          {activeDnsRecord && (
            <div className="cd-two-col">

              {/* DNS Configuration Instructions — real tokens from backend */}
              <div className="cd-panel">
                <div className="cd-panel-header">
                  <p className="cd-panel-title">DNS Configuration Instructions</p>
                  <p className="cd-panel-sub">Add these records to your domain provider for <strong>{activeDnsRecord.domain}</strong></p>
                </div>

                {(activeDnsRecord.dnsRecords || []).map((rec, i) => (
                  <div key={i} className="cd-dns-record">
                    <div className="cd-dns-record-top">
                      <span className="cd-dns-record-name">
                        {rec.type === 'TXT'
                          ? 'TXT Verification Record'
                          : rec.type === 'ALIAS'
                            ? 'ALIAS Record (root domain)'
                            : 'CNAME Record'}
                      </span>
                      <span className="cd-badge cd-badge-required">Required</span>
                    </div>
                    <div className="cd-dns-row"><span className="cd-dns-key">Type:</span><span className="cd-dns-val">{rec.type}</span></div>
                    <div className="cd-dns-row"><span className="cd-dns-key">Name:</span><span className="cd-dns-val">{rec.name}</span></div>
                    <div className="cd-dns-row">
                      <span className="cd-dns-key">Value:</span>
                      <span className="cd-dns-val cd-dns-copyable">
                        {rec.value}
                        <button type="button" className="cd-copy-btn" onClick={() => navigator.clipboard?.writeText(rec.value)} title="Copy">
                          <FaCopy />
                        </button>
                      </span>
                    </div>
                  </div>
                ))}

                {activeDnsRecord.status === 'ACTIVE' && (
                  <div className="cd-dns-verified">
                    <FaCheckCircle className="cd-verified-icon" />
                    <div>
                      <p className="cd-verified-title">DNS Verified</p>
                      <p className="cd-verified-sub">Domain successfully configured</p>
                    </div>
                  </div>
                )}

                {activeDnsRecord.status === 'VERIFYING' && (
                  <div className="cd-dns-verified" style={{ background: '#fff7e6', borderColor: '#ffd591' }}>
                    <FaClock className="cd-verified-icon" style={{ color: '#fa8c16' }} />
                    <div>
                      <p className="cd-verified-title">Awaiting DNS Verification</p>
                      <p className="cd-verified-sub">
                        Add the records above; we'll detect them automatically (usually within 5 minutes, up to 48 hours).
                      </p>
                    </div>
                  </div>
                )}

                {activeDnsRecord.status === 'PROPAGATING' && (
                  <div className="cd-dns-verified" style={{ background: '#e6f7ff', borderColor: '#91d5ff' }}>
                    <FaClock className="cd-verified-icon" style={{ color: '#1890ff' }} />
                    <div>
                      <p className="cd-verified-title">Propagating to CloudFront</p>
                      <p className="cd-verified-sub">
                        Distribution {activeDnsRecord.cloudfrontDistributionId || ''} is deploying globally. Typically 5–15 minutes.
                      </p>
                    </div>
                  </div>
                )}

                {activeDnsRecord.status === 'FAILED' && (
                  <div className="cd-dns-verified" style={{ background: '#fff1f0', borderColor: '#ffa39e' }}>
                    <FaClock className="cd-verified-icon" style={{ color: '#cf1322' }} />
                    <div>
                      <p className="cd-verified-title">Verification Failed</p>
                      <p className="cd-verified-sub">
                        {activeDnsRecord.errorMessage || 'Validation timed out. Use Reissue SSL to try again.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* SSL Certificate Status — real data from backend */}
              <div className="cd-panel">
                <div className="cd-panel-header">
                  <p className="cd-panel-title">SSL Certificate Status</p>
                  <p className="cd-panel-sub">Secure your custom domain with SSL</p>
                </div>

                <div className="cd-ssl-active-row">
                  <div className="cd-ssl-icon-wrap">
                    <FaShieldAlt className="cd-ssl-shield" />
                  </div>
                  <div className="cd-ssl-active-text">
                    <p className="cd-ssl-active-title">
                      {activeDnsRecord.sslStatus === 'ISSUED'
                        ? 'SSL Active'
                        : activeDnsRecord.sslStatus === 'REQUESTED'
                          ? 'SSL Requested'
                          : activeDnsRecord.sslStatus === 'FAILED'
                            ? 'SSL Failed'
                            : 'SSL Pending'}
                    </p>
                    <p className="cd-ssl-active-sub">
                      {activeDnsRecord.sslStatus === 'ISSUED'
                        ? 'Certificate is valid and secure'
                        : activeDnsRecord.sslStatus === 'REQUESTED'
                          ? 'ACM is validating the certificate'
                          : activeDnsRecord.sslStatus === 'FAILED'
                            ? activeDnsRecord.errorMessage || 'Validation failed — use Reissue SSL'
                            : 'Awaiting domain verification'}
                    </p>
                  </div>
                  <span className={`cd-badge ${activeDnsRecord.sslStatus === 'ISSUED' ? 'cd-badge-active' : 'cd-badge-pending'}`}>
                    {activeDnsRecord.sslStatus === 'ISSUED' ? 'Active' : activeDnsRecord.sslStatus === 'FAILED' ? 'Failed' : 'Pending'}
                  </span>
                </div>

                <div className="cd-ssl-details">
                  <div className="cd-ssl-detail-row">
                    <span className="cd-ssl-detail-key">Certificate Issuer</span>
                    <span className="cd-ssl-detail-val">Amazon (ACM)</span>
                  </div>
                  <div className="cd-ssl-detail-row">
                    <span className="cd-ssl-detail-key">Issued Date</span>
                    <span className="cd-ssl-detail-val">
                      {activeDnsRecord.sslIssuedAt ? new Date(activeDnsRecord.sslIssuedAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </span>
                  </div>
                  <div className="cd-ssl-detail-row">
                    <span className="cd-ssl-detail-key">Expiry Date</span>
                    <span className="cd-ssl-detail-val">
                      {activeDnsRecord.sslExpiresAt ? new Date(activeDnsRecord.sslExpiresAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </span>
                  </div>
                  <div className="cd-ssl-detail-row">
                    <span className="cd-ssl-detail-key">Auto-Renew</span>
                    <span className="cd-badge cd-badge-enabled">Enabled</span>
                  </div>
                </div>

                <button type="button" className="cd-reissue-btn" onClick={() => handleReissueSSL(activeDnsRecord.id)}>
                  <FaGlobe style={{ marginRight: 6 }} />
                  Reissue SSL Certificate
                </button>
              </div>
            </div>
          )}

          {/* All Custom Domains list — real data from backend */}
          <div className="cd-panel cd-panel-full">
            <div className="cd-panel-header">
              <p className="cd-panel-title">Active Custom Domains</p>
              <p className="cd-panel-sub">Manage your configured domains</p>
            </div>

            {customDomainsLoading ? (
              <p className="st-muted">Loading domains...</p>
            ) : customDomains.length === 0 ? (
              <p className="st-muted">No custom domains configured yet. Add one above.</p>
            ) : (
              <div className="cd-domain-list">
                {customDomains.map((d) => {
                  const isActive = d.status === 'ACTIVE';
                  const isFailed = d.status === 'FAILED';
                  const isInFlight = d.status === 'VERIFYING' || d.status === 'PROPAGATING';
                  const portalLabel = d.portalType === 'ADMIN'
                    ? 'Admin Portal'
                    : d.portalType === 'EMPLOYEE'
                      ? 'Employee Portal'
                      : 'Client Portal';
                  const statusLabel =
                    d.status === 'ACTIVE' ? 'Active'
                    : d.status === 'VERIFYING' ? 'Verifying DNS'
                    : d.status === 'PROPAGATING' ? 'Propagating'
                    : d.status === 'FAILED' ? 'Failed'
                    : 'Pending';
                  const badgeClass = isActive ? 'cd-badge-active' : isFailed ? 'cd-badge-pending' : 'cd-badge-pending';
                  return (
                    <div key={d.id} className="cd-domain-row">
                      <div className={`cd-domain-icon ${isActive ? 'cd-domain-icon-active' : 'cd-domain-icon-pending'}`}>
                        {isActive ? <FaCheckCircle /> : <FaClock />}
                      </div>
                      <div className="cd-domain-info">
                        <p className="cd-domain-name">{d.domain}</p>
                        <p className="cd-domain-meta">
                          {portalLabel} · {statusLabel}
                          {d.errorMessage ? ` · ${d.errorMessage.slice(0, 80)}` : ''}
                        </p>
                      </div>
                      <div className="cd-domain-actions">
                        <span className={`cd-badge ${badgeClass}`}>{statusLabel}</span>
                        {!isActive && !isFailed && (
                          <button type="button" className="cd-action-btn" onClick={() => handleVerifyDomain(d.id)}>
                            {isInFlight ? 'Check Now' : 'Verify'}
                          </button>
                        )}
                        <button type="button" className="cd-action-btn" onClick={() => setActiveDnsRecord(d)}>
                          View DNS
                        </button>
                        {isFailed && (
                          <button type="button" className="cd-action-btn" onClick={() => handleReissueSSL(d.id)}>
                            Reissue
                          </button>
                        )}
                        <button type="button" className="cd-action-btn cd-action-btn-danger" onClick={() => handleRemoveDomain(d.id)}>
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      )}

      {isPasswordModalOpen ? (
        <div className="acct-modal-overlay" onClick={closePasswordModal}>
          <div className="acct-modal" onClick={(event) => event.stopPropagation()}>
            <div className="acct-modal-header">
              <h3>Change Password</h3>
              <button type="button" className="acct-modal-close" onClick={closePasswordModal} aria-label="Close">
                <FaTimes />
              </button>
            </div>
            <form className="acct-password-form" onSubmit={handleChangePassword}>
              <label htmlFor="current-password">
                Current Password
                <div className="acct-password-field">
                  <input
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="acct-password-eye"
                    onClick={() => setShowCurrentPassword((v) => !v)}
                    aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                  >
                    {showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </label>
              <label htmlFor="new-password">
                New Password
                <div className="acct-password-field">
                  <input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(event) => {
                      setNewPassword(event.target.value);
                      setPasswordError('');
                    }}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="acct-password-eye"
                    onClick={() => setShowNewPassword((v) => !v)}
                    aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                  >
                    {showNewPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </label>
              <label htmlFor="confirm-password">
                Confirm New Password
                <div className="acct-password-field">
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(event) => {
                      setConfirmPassword(event.target.value);
                      setPasswordError('');
                    }}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="acct-password-eye"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </label>

              <div className="acct-password-rules">
                <p className="acct-password-rules-title">Password Requirements</p>
                <ul className="acct-password-checklist">
                  <li className={passwordChecks.length ? 'pass' : 'fail'}>
                    Length {PASSWORD_RULES.minLength}–{PASSWORD_RULES.maxLength} characters
                    <span>{passwordChecks.length ? '✓' : '✗'}</span>
                  </li>
                  <li className={passwordChecks.caps ? 'pass' : 'fail'}>
                    At least {PASSWORD_RULES.minCaps} uppercase letter
                    <span>{passwordChecks.caps ? '✓' : '✗'}</span>
                  </li>
                  <li className={passwordChecks.special ? 'pass' : 'fail'}>
                    At least {PASSWORD_RULES.minSpecial} special character
                    <span>{passwordChecks.special ? '✓' : '✗'}</span>
                  </li>
                  <li className={passwordChecks.digits ? 'pass' : 'fail'}>
                    At least {PASSWORD_RULES.minDigits} digit
                    <span>{passwordChecks.digits ? '✓' : '✗'}</span>
                  </li>
                  <li className={passwordChecks.match ? 'pass' : 'fail'}>
                    New password matches confirmation
                    <span>{passwordChecks.match ? '✓' : '✗'}</span>
                  </li>
                </ul>
                <p className="acct-password-rules-status">
                  Live check: {passwordMetrics.caps} caps, {passwordMetrics.special} special, {passwordMetrics.digits}{' '}
                  digits, {passwordMetrics.length} chars
                </p>
              </div>

              {passwordError ? <p className="acct-password-message error">{passwordError}</p> : null}
              {passwordSuccess ? <p className="acct-password-message success">{passwordSuccess}</p> : null}

              <div className="acct-password-actions">
                <button type="button" className="acct-btn-secondary" onClick={closePasswordModal} disabled={isSubmittingPassword}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="acct-btn-primary"
                  disabled={isSubmittingPassword || !currentPassword || !passwordMeetsRules}
                >
                  {isSubmittingPassword ? 'Saving...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

